/**
 * Payment schedule breakdown for proposals.
 *
 * The contract is billed on fixed percentage installments (50% signing /
 * 40% before shipment / 10% after install). Approved change orders either:
 *  - bill immediately (own sales order + QB invoice) and appear as their own
 *    schedule line, or
 *  - fold into the contract terms: the delta is allocated proportionally
 *    across the not-yet-due installments (credits always fold).
 *
 * Pure module — no firebase imports — so it is unit-testable and shareable
 * between the public proposal page, portal, builder, and timeline flags.
 */

import type { ChangeOrder, ChangeOrderBillingMode } from '../types';
import { invoiceClosesMilestone, type MilestoneInvoiceRef } from './partialInvoiceAllocation';

export type PaymentInstallmentId = 'deposit' | 'pre_shipment' | 'final';

export interface PaymentInstallmentDef {
  id: PaymentInstallmentId;
  percent: number;
  label: string;
  detail: string;
}

export const PAYMENT_INSTALLMENTS: PaymentInstallmentDef[] = [
  { id: 'deposit', percent: 0.5, label: '50%', detail: 'Deposit due upon contract signing' },
  { id: 'pre_shipment', percent: 0.4, label: '40%', detail: 'Due prior to shipment' },
  { id: 'final', percent: 0.1, label: '10%', detail: 'Due at delivery / completed installation' },
];

export interface ProgressBillingInvoiceRow {
  id: string;
  /** "50% deposit" */
  label: string;
  detail: string;
  percent: number;
  amount: number;
  invoiceId?: string;
  invoiceNum?: string;
  status?: string;
}

/** Installment rows for a progress-billing sales order (invoiced or still upcoming). */
export function progressBillingInvoiceRows(order: {
  grandTotal?: number;
  quickbooksInvoiceId?: string;
  quickbooksInvoiceNum?: string;
  installmentInvoices?: Array<MilestoneInvoiceRef & { installmentId?: string }>;
} | null | undefined): ProgressBillingInvoiceRow[] {
  const total = Number(order?.grandTotal) || 0;
  const entries = order?.installmentInvoices || [];
  const rows: ProgressBillingInvoiceRow[] = [];
  PAYMENT_INSTALLMENTS.forEach((def, index) => {
    const mine = entries.filter((e) => e.installmentId === def.id && e.status !== 'voided' && e.status !== 'removed_in_qb');
    const partials = mine.filter((e) => !invoiceClosesMilestone(e));
    const short =
      def.id === 'deposit' ? 'deposit' : def.id === 'pre_shipment' ? 'before shipment' : 'at delivery';
    const scheduled = index === PAYMENT_INSTALLMENTS.length - 1
      ? Math.max(0, Math.round(total) - Math.round(total * 0.5) - Math.round(total * 0.4))
      : Math.round(total * def.percent);
    if (partials.length === 0) {
      const entry = mine[0];
      const useHeaderInvoice = def.id === 'deposit' && !entry && !!order?.quickbooksInvoiceId;
      rows.push({
        id: def.id,
        label: `${def.label} ${short}`,
        detail: def.detail,
        percent: def.percent,
        amount: entry?.amount != null ? Math.round(entry.amount) : scheduled,
        invoiceId: entry?.quickbooksInvoiceId || (useHeaderInvoice ? order?.quickbooksInvoiceId : undefined),
        invoiceNum: entry?.quickbooksInvoiceNum || (useHeaderInvoice ? order?.quickbooksInvoiceNum : undefined),
        status: entry?.status,
      });
      return;
    }
    partials.forEach((entry, partialIndex) => {
      const groups = (entry.structureGroupLabels || []).filter(Boolean);
      rows.push({
        id: `${def.id}:${entry.partialId || entry.quickbooksInvoiceId || partialIndex}`,
        label: groups.length ? `${def.label} ${groups.join(', ')}` : `${def.label} ${short}`,
        detail: def.detail,
        percent: def.percent,
        amount: Math.round(Number(entry.amount) || 0),
        invoiceId: entry.quickbooksInvoiceId,
        invoiceNum: entry.quickbooksInvoiceNum,
        status: entry.status,
      });
    });
    const billed = mine.reduce((sum, entry) => sum + Math.round(Number(entry.amount) || 0), 0);
    const closed = mine.some((entry) => invoiceClosesMilestone(entry));
    if (!closed && scheduled - billed > 0) {
      rows.push({
        id: `${def.id}:remaining`,
        label: `${def.label} ${short} remaining`,
        detail: `${def.detail} — not yet invoiced`,
        percent: def.percent,
        amount: scheduled - billed,
      });
    }
  });
  return rows;
}

/**
 * Billing treatment for an approved change order. Credits (negative deltas)
 * always fold into contract terms — QB cannot issue negative invoices.
 * Missing `billingMode` means legacy 'immediate'.
 */
export function resolveChangeOrderBillingMode(
  co: Pick<ChangeOrder, 'billingMode' | 'deltaAmount'>,
): ChangeOrderBillingMode {
  if ((Number(co.deltaAmount) || 0) < 0) return 'contract_terms';
  return co.billingMode === 'contract_terms' ? 'contract_terms' : 'immediate';
}

/** True when approving this CO should create a sales order + QB invoice. */
export function changeOrderBillsImmediately(
  co: Pick<ChangeOrder, 'billingMode' | 'deltaAmount'>,
): boolean {
  return resolveChangeOrderBillingMode(co) === 'immediate';
}

/** "CO-001"-style reference without importing firestore-backed helpers. */
function coReference(co: Pick<ChangeOrder, 'reference' | 'projectNumber' | 'number'>): string {
  if (co.reference) return co.reference;
  const n = `CO-${String(Math.max(1, Math.floor(Number(co.number) || 1))).padStart(3, '0')}`;
  const pn = (co.projectNumber || '').trim();
  return pn ? `${pn}-${n}` : n;
}

export interface PaymentScheduleCoAdjustment {
  changeOrderId?: string;
  reference: string;
  title: string;
  /** Signed portion of the CO delta allocated to this installment. */
  amount: number;
}

export interface PaymentScheduleRow {
  kind: 'installment' | 'change_order';
  id: string;
  label: string;
  detail: string;
  /** Base percentage for installment rows (0.5 / 0.4 / 0.1). */
  percent?: number;
  /** Percent × contract base for installments; CO delta for change_order rows. */
  baseAmount: number;
  /** Folded CO allocations applied to this installment. */
  coAdjustments: PaymentScheduleCoAdjustment[];
  /** Final amount due for this row (base + adjustments, clamped ≥ 0 for installments). */
  amount: number;
  /** Whether this installment's due point has already passed. */
  passed: boolean;
}

export interface PaymentScheduleBreakdown {
  rows: PaymentScheduleRow[];
  /** Signed proposal base (or live total pre-signing). */
  baseTotal: number;
  /** Sum of folded (contract-terms) approved CO deltas. */
  foldedCoTotal: number;
  /** Sum of immediately billed approved CO deltas. */
  immediateCoTotal: number;
  /** baseTotal + all approved CO deltas. */
  currentContractValue: number;
  /** Credit remaining after all unpaid installments clamp at zero — owed to the client. */
  refundDue: number;
  hasChangeOrders: boolean;
}

export interface InvoicedInstallmentLock {
  installmentId: PaymentInstallmentId;
  /** When that installment's invoice was created (paidAt is a fallback). */
  invoicedAt: Date | string | number | { toDate?: () => Date; toMillis?: () => number };
}

export interface BuildPaymentScheduleOptions {
  /**
   * Installments whose due point has passed — folded COs allocate only across
   * the rest. Deposit is treated as passed by default (COs post-date signing).
   */
  passedInstallmentIds?: PaymentInstallmentId[];
  /**
   * Installments already locked by an invoice. A folded change order approved
   * after that lock cannot change the invoiced installment — the whole delta
   * lands on installments that were still open.
   */
  invoicedInstallments?: InvoicedInstallmentLock[];
}

interface WorkingInstallment {
  def: PaymentInstallmentDef;
  baseAmount: number;
  current: number;
  adjustments: PaymentScheduleCoAdjustment[];
  passed: boolean;
}

function isApproved(co: Pick<ChangeOrder, 'status'>): boolean {
  return co.status === 'approved';
}

function approvalOrder(a: ChangeOrder, b: ChangeOrder): number {
  return toScheduleMs(a.approvedAt) - toScheduleMs(b.approvedAt) || (a.number || 0) - (b.number || 0);
}

export function toScheduleMs(v: unknown): number {
  if (!v) return 0;
  if (v instanceof Date) return v.getTime();
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const parsed = Date.parse(v);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  const maybe = v as { toMillis?: () => number; toDate?: () => Date };
  if (typeof maybe.toMillis === 'function') return maybe.toMillis();
  if (typeof maybe.toDate === 'function') return maybe.toDate().getTime();
  return 0;
}

/**
 * Invoice lock times for folded change orders. Prefers the invoice create
 * time, then paidAt, so a credit approved after the bill cannot reopen it.
 */
export function invoicedInstallmentLocks(
  records:
    | Array<{
        installmentId?: string;
        status?: string;
        createdAt?: unknown;
        paidAt?: unknown;
        invoicedAt?: unknown;
        structureIds?: string[];
        partialId?: string;
        closesMilestone?: boolean;
      }>
    | null
    | undefined,
): InvoicedInstallmentLock[] {
  const locks: InvoicedInstallmentLock[] = [];
  for (const row of records || []) {
    if (!invoiceClosesMilestone(row)) continue;
    const id = row.installmentId;
    if (id !== 'deposit' && id !== 'pre_shipment' && id !== 'final') continue;
    const invoicedAt = toScheduleMs(row.invoicedAt || row.createdAt || row.paidAt);
    if (invoicedAt <= 0) continue;
    locks.push({ installmentId: id, invoicedAt });
  }
  return locks;
}

function invoicedAtByInstallment(
  locks: InvoicedInstallmentLock[] | undefined,
): Map<PaymentInstallmentId, number> {
  const map = new Map<PaymentInstallmentId, number>();
  for (const lock of locks || []) {
    const at = toScheduleMs(lock.invoicedAt);
    if (at > 0) map.set(lock.installmentId, at);
  }
  return map;
}

function lockedInstallmentsForChangeOrder(
  staticPassed: Set<PaymentInstallmentId>,
  invoicedAt: Map<PaymentInstallmentId, number>,
  approvedAtMs: number,
): Set<PaymentInstallmentId> {
  const locked = new Set(staticPassed);
  if (approvedAtMs <= 0) return locked;
  invoicedAt.forEach((at, id) => {
    if (at <= approvedAtMs) locked.add(id);
  });
  return locked;
}

/**
 * Allocate a folded CO delta across installments proportionally by percent.
 * Positive deltas split cleanly; credits clamp each installment at zero and
 * cascade the excess to installments that still have balance. Returns any
 * un-allocatable credit remainder (refund due).
 */
function allocateFoldedDelta(
  delta: number,
  targets: WorkingInstallment[],
  adjustment: Omit<PaymentScheduleCoAdjustment, 'amount'>,
  lockedIds?: Set<PaymentInstallmentId>,
): number {
  const isLocked = (t: WorkingInstallment) => (lockedIds ? lockedIds.has(t.def.id) : t.passed);
  let eligible = targets.filter(t => !isLocked(t));
  if (eligible.length === 0) {
    // Everything has passed — fold into the final installment regardless.
    const final = targets[targets.length - 1];
    if (!final) return delta < 0 ? -delta : 0;
    eligible = [final];
  }

  const applied = new Map<PaymentInstallmentId, number>();

  if (delta >= 0) {
    const totalWeight = eligible.reduce((s, t) => s + t.def.percent, 0) || 1;
    let assigned = 0;
    eligible.forEach((t, i) => {
      const share =
        i === eligible.length - 1
          ? delta - assigned
          : Math.round(delta * (t.def.percent / totalWeight));
      assigned += share;
      t.current += share;
      applied.set(t.def.id, (applied.get(t.def.id) || 0) + share);
    });
  } else {
    // Credit: proportional passes with per-installment clamping at zero.
    let remaining = delta;
    let active = eligible.filter(t => t.current > 0);
    let guard = 0;
    while (remaining < -0.5 && active.length > 0 && guard < 10) {
      guard += 1;
      const totalWeight = active.reduce((s, t) => s + t.def.percent, 0) || 1;
      const passTotal = remaining;
      let progressed = false;
      for (const t of active) {
        const share = Math.round(passTotal * (t.def.percent / totalWeight));
        const take = Math.max(share, -t.current);
        if (take !== 0) progressed = true;
        t.current += take;
        remaining -= take;
        applied.set(t.def.id, (applied.get(t.def.id) || 0) + take);
      }
      active = active.filter(t => t.current > 0);
      if (!progressed) break;
    }
    // Sweep sub-dollar residue into any installment with balance.
    if (remaining < 0) {
      const sink = eligible.find(t => t.current > 0);
      if (sink) {
        const take = Math.max(remaining, -sink.current);
        sink.current += take;
        remaining -= take;
        applied.set(sink.def.id, (applied.get(sink.def.id) || 0) + take);
      }
    }
    if (remaining < 0) {
      recordAdjustments(targets, applied, adjustment);
      return -remaining;
    }
  }

  recordAdjustments(targets, applied, adjustment);
  return 0;
}

function recordAdjustments(
  targets: WorkingInstallment[],
  applied: Map<PaymentInstallmentId, number>,
  adjustment: Omit<PaymentScheduleCoAdjustment, 'amount'>,
): void {
  for (const t of targets) {
    const amount = applied.get(t.def.id);
    if (amount) t.adjustments.push({ ...adjustment, amount });
  }
}

/**
 * Build the full payment schedule breakdown.
 *
 * @param baseTotal signed proposal base (or live grand total pre-signing)
 * @param changeOrders all COs for the deal (non-approved ones are ignored)
 */
export function buildPaymentScheduleBreakdown(
  baseTotal: number,
  changeOrders?: ChangeOrder[] | null,
  options?: BuildPaymentScheduleOptions,
): PaymentScheduleBreakdown {
  const base = Math.max(0, Math.round(Number(baseTotal) || 0));
  const passed = new Set<PaymentInstallmentId>(options?.passedInstallmentIds ?? ['deposit']);
  const invoicedAt = invoicedAtByInstallment(options?.invoicedInstallments);

  // Base installment amounts sum exactly to the base (final takes the remainder).
  const working: WorkingInstallment[] = [];
  let assigned = 0;
  PAYMENT_INSTALLMENTS.forEach((def, i) => {
    const amount = i === PAYMENT_INSTALLMENTS.length - 1 ? base - assigned : Math.round(base * def.percent);
    assigned += amount;
    working.push({
      def,
      baseAmount: amount,
      current: amount,
      adjustments: [],
      passed: passed.has(def.id),
    });
  });

  const approved = (changeOrders || []).filter(isApproved).slice().sort(approvalOrder);
  const folded = approved.filter(co => resolveChangeOrderBillingMode(co) === 'contract_terms');
  const immediate = approved.filter(co => resolveChangeOrderBillingMode(co) === 'immediate');

  let refundDue = 0;
  for (const co of folded) {
    const locked = lockedInstallmentsForChangeOrder(passed, invoicedAt, toScheduleMs(co.approvedAt));
    refundDue += allocateFoldedDelta(
      Math.round(Number(co.deltaAmount) || 0),
      working,
      {
        changeOrderId: co.id,
        reference: coReference(co),
        title: co.title || 'Change order',
      },
      locked,
    );
  }

  const rows: PaymentScheduleRow[] = working.map(w => ({
    kind: 'installment',
    id: w.def.id,
    label: w.def.label,
    detail: w.def.detail,
    percent: w.def.percent,
    baseAmount: w.baseAmount,
    coAdjustments: w.adjustments,
    amount: Math.max(0, Math.round(w.current)),
    passed: w.passed,
  }));

  for (const co of immediate) {
    rows.push({
      kind: 'change_order',
      id: `co-${co.id || co.number}`,
      label: coReference(co),
      detail: co.title ? `${co.title} — due upon approval` : 'Due upon approval',
      baseAmount: Math.round(Number(co.deltaAmount) || 0),
      coAdjustments: [],
      amount: Math.round(Number(co.deltaAmount) || 0),
      passed: true,
    });
  }

  const foldedCoTotal = folded.reduce((s, co) => s + Math.round(Number(co.deltaAmount) || 0), 0);
  const immediateCoTotal = immediate.reduce((s, co) => s + Math.round(Number(co.deltaAmount) || 0), 0);

  return {
    rows,
    baseTotal: base,
    foldedCoTotal,
    immediateCoTotal,
    currentContractValue: base + foldedCoTotal + immediateCoTotal,
    refundDue: Math.round(refundDue),
    hasChangeOrders: approved.length > 0,
  };
}

/** First payment was taken as a legacy full invoice before progress billing was enabled. */
export function legacyDepositSatisfiedFromSalesOrder(
  so:
    | {
        quickbooksInvoiceId?: string;
        installmentInvoices?: Array<{ installmentId: PaymentInstallmentId; status?: string }>;
      }
    | null
    | undefined,
): boolean {
  if (!so?.quickbooksInvoiceId) return false;
  const billed = new Set(
    (so.installmentInvoices ?? [])
      .filter(e => e.status !== 'voided')
      .map(e => e.installmentId),
  );
  return !billed.has('deposit');
}

/**
 * Legacy lump-sum invoice covered the whole contract (not just the 50% deposit).
 * Requires a synced QB invoice that is actually paid in full.
 */
export function legacyFullPaymentSatisfiedFromSalesOrder(
  so:
    | {
        billingStyle?: string;
        quickbooksInvoiceId?: string;
        grandTotal?: number;
        installmentInvoices?: Array<{ installmentId: PaymentInstallmentId; status?: string }>;
      }
    | null
    | undefined,
  options?: { qbInvoiceTotal?: number; qbInvoicePaid?: boolean },
): boolean {
  if (!so?.quickbooksInvoiceId) return false;
  // Progress billing invoices per installment; anything else (explicit 'full'
  // or legacy orders with no billingStyle at all) can be a lump-sum invoice.
  if (so.billingStyle && so.billingStyle !== 'full') return false;
  const installments = (so.installmentInvoices ?? []).filter((e) => e.status !== 'voided');
  if (installments.length > 0) return false;

  const contract = Math.round(Number(so.grandTotal) || 0);
  if (contract <= 0) return false;

  const invoiceTotal = Math.round(Number(options?.qbInvoiceTotal) || 0);
  if (invoiceTotal <= 0) return false;
  if (options?.qbInvoicePaid !== true) return false;

  const near = (a: number, b: number) =>
    Math.abs(a - b) <= Math.max(250, Math.round(contract * 0.06));

  if (near(invoiceTotal, Math.round(contract * 0.5))) return false;
  return invoiceTotal >= contract * 0.85;
}

/**
 * Installment IDs treated as passed for schedule folding — mirrors the client portal
 * (deposit assumed passed once signed) plus any billed installments and legacy deposit.
 */
export function resolveSchedulePassedInstallmentIds(options: {
  installmentInvoices?: Array<{ installmentId: PaymentInstallmentId; status?: string }>;
  legacyDepositSatisfied?: boolean;
  /** When true (default), deposit is passed once contract is signed. */
  contractSigned?: boolean;
}): PaymentInstallmentId[] {
  const passed = new Set<PaymentInstallmentId>();
  if (options.contractSigned !== false) passed.add('deposit');
  for (const e of options.installmentInvoices ?? []) {
    if (e.status !== 'voided') passed.add(e.installmentId);
  }
  if (options.legacyDepositSatisfied) passed.add('deposit');
  return Array.from(passed);
}

export function effectiveBilledInstallmentIds(
  so:
    | {
        quickbooksInvoiceId?: string;
        installmentInvoices?: Array<{ installmentId: PaymentInstallmentId; status?: string }>;
      }
    | null
    | undefined,
): Set<PaymentInstallmentId> {
  const ids = new Set<PaymentInstallmentId>(
    (so?.installmentInvoices ?? [])
      .filter(e => invoiceClosesMilestone(e))
      .map(e => e.installmentId),
  );
  if (legacyDepositSatisfiedFromSalesOrder(so)) ids.add('deposit');
  return ids;
}

const PROGRESS_INVOICE_ORDER = ['deposit', 'pre_shipment', 'final'] as const;

/**
 * Ready to bill creates a QuickBooks invoice only for the next unbilled
 * progress installment on a job that was not opened with a legacy deposit.
 */
export function shouldAutoCreateProgressInvoice(args: {
  kind: 'installment' | 'change_order' | 'custom';
  installmentId?: string;
  salesOrder:
    | {
        isChangeOrder?: boolean;
        billingStyle?: string;
        quickbooksInvoiceId?: string;
        installmentInvoices?: Array<{ installmentId: PaymentInstallmentId; status?: string }>;
      }
    | null
    | undefined;
}): boolean {
  if (args.kind !== 'installment') return false;
  const id = args.installmentId;
  if (id !== 'deposit' && id !== 'pre_shipment' && id !== 'final') return false;
  const so = args.salesOrder;
  if (!so || so.isChangeOrder) return false;
  if (legacyDepositSatisfiedFromSalesOrder(so)) return false;
  if (!isEffectiveProgressBilling(so)) return false;
  const billed = effectiveBilledInstallmentIds(so);
  const next = PROGRESS_INVOICE_ORDER.find((step) => !billed.has(step));
  if (next === id) return true;
  // Delivery can be billed for packages whose pre-shipment invoice already
  // exists, even while other packages are still unbilled.
  if (id !== 'final' || billed.has('final')) return false;
  const preShipmentOpen = (so.installmentInvoices ?? []).some(
    (entry) => entry.installmentId === 'pre_shipment' && entry.status !== 'voided' && entry.status !== 'removed_in_qb',
  );
  return preShipmentOpen && (billed.has('deposit') || legacyDepositSatisfiedFromSalesOrder(so));
}

/** Progress billing is active, or a legacy deposit invoice satisfies the first milestone. */
export function isEffectiveProgressBilling(
  so:
    | {
        billingStyle?: string;
        quickbooksInvoiceId?: string;
        installmentInvoices?: Array<{ installmentId: PaymentInstallmentId }>;
      }
    | null
    | undefined,
): boolean {
  if (!so) return false;
  if (so.billingStyle === 'progress') return true;
  return legacyDepositSatisfiedFromSalesOrder(so);
}
